
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Brain, TrendingUp, Users, AlertTriangle, Sparkles, Target } from "lucide-react";
import { useMLAnalytics } from "@/hooks/useMLAnalytics";
import { motion } from "framer-motion";

const MLInsightsDashboard = () => {
  const { mlData, isLoading, refetch } = useMLAnalytics();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Brain className="h-12 w-12 text-purple-600 mx-auto mb-4 animate-pulse" />
            <h3 className="text-lg font-semibold text-admin-foreground mb-2">
              AI Processing Analytics
            </h3>
            <p className="text-admin-muted-foreground">
              Machine learning models are analyzing your data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!mlData) return null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Brain className="h-8 w-8 text-purple-600" />
          <div>
            <h2 className="text-2xl font-bold text-admin-foreground">AI Insights Dashboard</h2>
            <p className="text-admin-muted-foreground">Machine learning powered business intelligence</p>
          </div>
        </div>
        <Button onClick={refetch} className="bg-purple-600 hover:bg-purple-700">
          <Sparkles className="h-4 w-4 mr-2" />
          Refresh Insights
        </Button>
      </div>

      {/* Sales Forecast */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>AI Sales Forecast</span>
            <Badge variant="secondary">
              {Math.round(mlData.salesForecast.confidence * 100)}% confidence
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div 
              className="space-y-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <div className="flex justify-between">
                <span className="text-admin-muted-foreground">Next Week Prediction</span>
                <span className="font-semibold text-admin-foreground">
                  ${mlData.salesForecast.nextWeek.toFixed(2)}
                </span>
              </div>
              <Progress value={mlData.salesForecast.confidence * 100} className="h-2" />
            </motion.div>
            <motion.div 
              className="space-y-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex justify-between">
                <span className="text-admin-muted-foreground">Next Month Prediction</span>
                <span className="font-semibold text-admin-foreground">
                  ${mlData.salesForecast.nextMonth.toFixed(2)}
                </span>
              </div>
              <Progress value={mlData.salesForecast.confidence * 100} className="h-2" />
            </motion.div>
          </div>
        </CardContent>
      </Card>

      {/* Customer Segments */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground flex items-center space-x-2">
            <Users className="h-5 w-5" />
            <span>AI Customer Segmentation</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mlData.customerSegments.map((segment, index) => (
              <motion.div
                key={segment.segment}
                className="border border-admin-border rounded-lg p-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex justify-between items-start mb-3">
                  <h4 className="font-semibold text-admin-foreground">{segment.segment}</h4>
                  <Badge variant="outline">{segment.size} customers</Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-admin-muted-foreground">Total Value</span>
                    <p className="text-lg font-semibold text-admin-foreground">
                      ${segment.value.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="text-sm text-admin-muted-foreground">Characteristics</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {segment.characteristics.map((char, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">
                          {char}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Product Recommendations */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>AI Product Recommendations</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {mlData.productRecommendations.map((rec, index) => (
              <motion.div
                key={rec.productId}
                className="flex items-center justify-between p-3 border border-admin-border rounded-lg"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <div>
                  <h5 className="font-medium text-admin-foreground">{rec.productName}</h5>
                  <p className="text-sm text-admin-muted-foreground">{rec.reason}</p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-admin-foreground">{rec.predictedSales} sales</p>
                  <p className="text-xs text-admin-muted-foreground">predicted</p>
                </div>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Anomaly Detection */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5" />
            <span>Anomaly Detection</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {mlData.anomalies.map((anomaly, index) => (
              <motion.div
                key={index}
                className={`p-3 rounded-lg border ${
                  anomaly.detected 
                    ? anomaly.severity === 'high' 
                      ? 'border-red-200 bg-red-50' 
                      : anomaly.severity === 'medium'
                      ? 'border-yellow-200 bg-yellow-50'
                      : 'border-blue-200 bg-blue-50'
                    : 'border-green-200 bg-green-50'
                }`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-2">
                      <Badge 
                        variant={anomaly.detected ? "destructive" : "secondary"}
                        className="capitalize"
                      >
                        {anomaly.type}
                      </Badge>
                      {anomaly.detected && (
                        <Badge variant="outline" className="capitalize">
                          {anomaly.severity}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm mt-1 text-gray-700">{anomaly.description}</p>
                  </div>
                  <div className={`w-3 h-3 rounded-full ${
                    anomaly.detected 
                      ? anomaly.severity === 'high' 
                        ? 'bg-red-500' 
                        : anomaly.severity === 'medium'
                        ? 'bg-yellow-500'
                        : 'bg-blue-500'
                      : 'bg-green-500'
                  }`} />
                </div>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MLInsightsDashboard;
